<template>
    <v-container :fluid="true">
        <v-row>
            <v-col class="mx-auto" cols="12" sm="10" lg="8">

                <v-tabs v-model="tab" class="mb-4">
                    <v-tab value="config">Webhook</v-tab>
                    <v-tab value="history">Delivery History</v-tab>
                    <v-tab value="api-keys">API Keys</v-tab>
                </v-tabs>

                <!-- ── Configuration tab ── -->
                <v-window v-model="tab">
                <v-window-item value="config">
                <v-card elevation="3">
                    <v-card-title class="text-start font-weight-bold">Webhook Integration</v-card-title>
                    <v-card-subtitle class="pb-2">
                        Send events to your own API endpoint in real time.
                    </v-card-subtitle>

                    <v-card-text>
                        <v-alert v-if="saveError" type="error" density="compact" class="mb-4" closable
                                 @click:close="saveError = null">
                            {{ saveError }}
                        </v-alert>
                        <v-alert v-if="!form.is_active && form.consecutive_failures >= 3"
                                 type="warning" density="compact" class="mb-4">
                            Webhook was automatically disabled after {{ form.consecutive_failures }} consecutive
                            delivery failures. Fix the endpoint URL and re-enable.
                        </v-alert>

                        <v-switch v-model="form.is_active" color="primary" label="Enable webhook"
                                  density="compact" hide-details class="mb-4"/>

                        <v-text-field v-model="form.webhook_url" label="Webhook URL" density="compact"
                                      placeholder="https://your-api.example.com/webhook"
                                      :rules="[urlRule]" clearable class="mb-3"/>

                        <!-- Security code: never display the stored value back -->
                        <div class="mb-4">
                            <template v-if="existingHasSecurityCode && !rotatingSecret">
                                <div class="d-flex align-center">
                                    <v-text-field model-value="••••••••••••••••"
                                                  label="Security code (HMAC signing key)"
                                                  density="compact" readonly :hide-details="true" style="flex:1"/>
                                    <v-btn variant="text" size="small" class="ml-2"
                                           @click="rotatingSecret = true">Rotate</v-btn>
                                </div>
                                <div class="text-caption text-medium-emphasis mt-1">
                                    Used to sign payloads via HMAC-SHA256. Never sent in the payload body.
                                </div>
                            </template>
                            <template v-else>
                                <v-text-field v-model="form.security_code"
                                              :label="rotatingSecret ? 'New security code (min 16 characters)' : 'Security code (optional, min 16 chars)'"
                                              density="compact" clearable
                                              :append-inner-icon="showSecret ? 'mdi-eye-off' : 'mdi-eye'"
                                              :type="showSecret ? 'text' : 'password'"
                                              :hint="rotatingSecret ? 'Enter a new value to rotate the key.' : 'Used to sign payloads via HMAC-SHA256. Never sent in the payload body.'"
                                              persistent-hint
                                              @click:append-inner="showSecret = !showSecret"/>
                                <v-btn v-if="rotatingSecret" variant="text" size="small" class="mt-1"
                                       @click="rotatingSecret = false; form.security_code = null">
                                    Cancel rotation
                                </v-btn>
                            </template>
                        </div>

                        <v-text-field v-model="form.reference" label="Reference (optional)"
                                      density="compact" clearable maxlength="100" class="mb-5"
                                      hint="Your internal system identifier included in every payload"/>

                        <!-- Event type checkboxes -->
                        <div class="text-body-2 font-weight-medium mb-2">Events to deliver</div>
                        <v-row dense class="mb-1">
                            <v-col cols="12" class="text-caption text-medium-emphasis mb-1">Movement</v-col>
                            <v-col cols="12" sm="4">
                                <v-checkbox v-model="form.event_movement_started" label="Trip started"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-checkbox v-model="form.event_movement_progress" label="Trip in progress"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-checkbox v-model="form.event_movement_ended" label="Trip ended"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                        </v-row>
                        <div v-if="form.event_movement_progress" class="text-caption text-medium-emphasis mb-3 ml-1">
                            Progress events throttled to at most one per trip per 5 minutes.
                        </div>

                        <v-row dense class="mb-4">
                            <v-col cols="12" class="text-caption text-medium-emphasis mb-1">Alerts</v-col>
                            <v-col cols="12" sm="3">
                                <v-checkbox v-model="form.event_geofence" label="Geofence"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-checkbox v-model="form.event_speed" label="Speed"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-checkbox v-model="form.event_battery" label="Battery"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-checkbox v-model="form.event_crash" label="Crash"
                                            density="compact" hide-details color="primary"/>
                            </v-col>
                        </v-row>

                        <!-- GDPR consent gate -->
                        <v-alert v-if="form.gdpr_consented" type="success" density="compact"
                                 variant="tonal" class="mb-4">
                            Data sharing consent recorded on
                            {{ form.gdpr_consented_at ? new Date(form.gdpr_consented_at).toLocaleDateString() : 'file' }}.
                        </v-alert>
                        <v-card v-else variant="outlined" class="mb-4 pa-3">
                            <v-checkbox v-model="gdprConsent" color="warning" hide-details class="mb-1"
                                        density="compact">
                                <template #label>
                                    <span class="text-body-2">
                                        I authorise SimpliGPS to send vehicle location, speed, battery,
                                        crash events, and asset details to the configured endpoint on
                                        behalf of my company.
                                    </span>
                                </template>
                            </v-checkbox>
                            <div class="text-caption text-medium-emphasis ml-8">
                                Required before live events can be delivered. Recorded with your name and
                                timestamp for compliance.
                            </div>
                        </v-card>
                    </v-card-text>

                    <v-card-actions class="px-4 pb-4 d-flex justify-space-between">
                        <div class="d-flex flex-column align-start">
                            <v-btn variant="outlined" size="small" :loading="testing"
                                   :disabled="!form.webhook_url || saving || testLimitReached"
                                   @click="testWebhook">
                                Send test event
                            </v-btn>
                            <div v-if="testLimitReached" class="text-caption text-error mt-1">
                                Rate limit reached — wait a minute.
                            </div>
                        </div>
                        <v-btn color="primary" size="small" :loading="saving"
                               :disabled="form.is_active && !form.gdpr_consented && !gdprConsent"
                               @click="save">
                            Save
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card elevation="2" class="mt-4 pa-4">
                    <v-card-title class="text-body-2 font-weight-bold pb-2">Payload format</v-card-title>
                    <div class="text-caption text-medium-emphasis mb-2">Request headers:</div>
                    <pre class="text-caption bg-grey-lighten-4 rounded pa-3 overflow-x-auto mb-3">{{ headerExample }}</pre>
                    <div class="text-caption text-medium-emphasis mb-2">Body (movement event):</div>
                    <pre class="text-caption bg-grey-lighten-4 rounded pa-3 overflow-x-auto mb-3">{{ movementPayloadExample }}</pre>
                    <div class="text-caption text-medium-emphasis mb-2">Body (alert event — geofence example):</div>
                    <pre class="text-caption bg-grey-lighten-4 rounded pa-3 overflow-x-auto">{{ alertPayloadExample }}</pre>
                    <div class="text-caption text-medium-emphasis mt-2">
                        Validate: <code>sha256=HMAC-SHA256(secret, raw_body)</code>.
                        Reject if <code>|now - timestamp| &gt; 300s</code>.
                    </div>
                </v-card>
                </v-window-item>

                <!-- ── Delivery history tab ── -->
                <v-window-item value="history">
                <v-card elevation="3">
                    <v-card-title class="text-start font-weight-bold d-flex align-center justify-space-between">
                        <span>Delivery History</span>
                        <v-btn variant="text" size="small" :loading="loadingLogs" @click="loadLogs">
                            Refresh
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-0">
                        <v-data-table
                            :headers="logHeaders"
                            :items="logs"
                            :loading="loadingLogs"
                            density="compact"
                            no-data-text="No deliveries recorded yet."
                            hide-default-footer
                            :items-per-page="-1"
                        >
                            <template #item.success="{ item }">
                                <v-chip :color="item.success ? 'success' : 'error'" size="x-small" label>
                                    {{ item.success ? 'OK' : 'FAIL' }}
                                </v-chip>
                            </template>
                            <template #item.http_status="{ item }">
                                {{ item.http_status ?? '—' }}
                            </template>
                            <template #item.source="{ item }">
                                <v-chip size="x-small" label variant="tonal"
                                        :color="item.asset_alert_id ? 'warning' : 'primary'">
                                    {{ item.asset_alert_id ? 'alert' : 'movement' }}
                                </v-chip>
                            </template>
                            <template #item.dispatched_at="{ item }">
                                {{ formatDate(item.dispatched_at) }}
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
                </v-window-item>

                <!-- ── API Keys tab ── -->
                <v-window-item value="api-keys">
                    <ApiCredentials/>
                </v-window-item>
                </v-window>

            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {reactive, ref, onMounted, watch} from "vue";
import ApiCredentials from "@/components/integrations/ApiCredentials.vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();

const tab = ref('config');

const form = reactive({
    is_active: false,
    webhook_url: null,
    security_code: null,
    reference: null,
    consecutive_failures: 0,
    gdpr_consented: false,
    gdpr_consented_at: null,
    event_movement_started: true,
    event_movement_progress: true,
    event_movement_ended: true,
    event_geofence: false,
    event_speed: false,
    event_battery: false,
    event_crash: false,
});

const existingHasSecurityCode = ref(false);
const rotatingSecret = ref(false);
const showSecret = ref(false);
const gdprConsent = ref(false);
const saving = ref(false);
const testing = ref(false);
const saveError = ref(null);
const testCount = ref(0);
const testLimitReached = ref(false);

const logs = ref([]);
const loadingLogs = ref(false);

const logHeaders = [
    {title: 'Time', key: 'dispatched_at', width: '160px'},
    {title: 'Event', key: 'event'},
    {title: 'Source', key: 'source', width: '100px'},
    {title: 'Status', key: 'success', width: '80px'},
    {title: 'HTTP', key: 'http_status', width: '70px'},
];

const urlRule = v => !v || /^https?:\/\/.+/.test(v) || 'Must be a valid URL';

const headerExample = [
    'Content-Type: application/json',
    'X-SimpliGPS-Signature: sha256=<HMAC-SHA256(secret, body)>',
    'X-SimpliGPS-Timestamp: 2026-07-30T14:30:00Z',
].join('\n');

const movementPayloadExample = JSON.stringify({
    event_id: "550e8400-e29b-41d4-a716-446655440000",
    event: "movement.started",
    timestamp: "2026-07-30T14:30:00Z",
    reference: "your-ref",
    asset: {name: "Truck 01", serial: "850005000303", customer_reference: "FLEET-001"},
    location: {latitude: 33.749, longitude: -84.388, accuracy_m: 5, speed_mph: 12.4, heading: 90},
}, null, 2);

const alertPayloadExample = JSON.stringify({
    event_id: "660e8400-e29b-41d4-a716-446655440000",
    event: "alert.geofence",
    timestamp: "2026-07-30T14:35:00Z",
    reference: "your-ref",
    asset: {name: "Truck 01", serial: "850005000303", customer_reference: "FLEET-001"},
    alert: {zone: "HQ Campus", direction: "entered"},
}, null, 2);

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
}

onMounted(async () => {
    try {
        const res = await send("get", "company-integrations");
        if (res.data?.success) {
            const data = res.data.data;
            Object.assign(form, data);
            existingHasSecurityCode.value = !!data.has_security_code;
            form.security_code = null;
        }
    } catch {
        // no integration yet — defaults are fine
    }
});

watch(tab, (val) => {
    if (val === 'history' && logs.value.length === 0) {
        loadLogs();
    }
});

async function loadLogs() {
    loadingLogs.value = true;
    try {
        const res = await send("get", "company-integrations/logs");
        if (res.data?.success) {
            logs.value = res.data.data.logs ?? [];
        }
    } catch {
        snackbar.showSnackbar("Failed to load delivery history.", "error");
    } finally {
        loadingLogs.value = false;
    }
}

async function save() {
    saveError.value = null;
    saving.value = true;
    try {
        const payload = {
            is_active: form.is_active,
            webhook_url: form.webhook_url,
            reference: form.reference,
            gdpr_consented: gdprConsent.value || form.gdpr_consented,
            event_movement_started: form.event_movement_started,
            event_movement_progress: form.event_movement_progress,
            event_movement_ended: form.event_movement_ended,
            event_geofence: form.event_geofence,
            event_speed: form.event_speed,
            event_battery: form.event_battery,
            event_crash: form.event_crash,
        };
        if (form.security_code) {
            payload.security_code = form.security_code;
        }

        const res = await send("post", "company-integrations", payload);
        if (res.data?.success) {
            const data = res.data.data;
            Object.assign(form, data);
            existingHasSecurityCode.value = !!data.has_security_code;
            form.security_code = null;
            rotatingSecret.value = false;
            snackbar.showSnackbar("Webhook settings saved.");
        } else {
            saveError.value = res.data?.message || "Failed to save settings.";
        }
    } catch (e) {
        saveError.value = e?.response?.data?.message || "Failed to save settings.";
    } finally {
        saving.value = false;
    }
}

async function testWebhook() {
    if (testLimitReached.value) return;
    testing.value = true;
    try {
        const res = await send("post", "company-integrations/test");
        if (res.data?.data?.success) {
            snackbar.showSnackbar("Test event delivered successfully.");
        } else {
            snackbar.showSnackbar("Test event failed — check the URL.", "error");
        }
        testCount.value++;
        if (testCount.value >= 5) {
            testLimitReached.value = true;
            setTimeout(() => { testLimitReached.value = false; testCount.value = 0; }, 60000);
        }
    } catch (e) {
        if (e?.response?.status === 429) {
            testLimitReached.value = true;
            setTimeout(() => { testLimitReached.value = false; testCount.value = 0; }, 60000);
            snackbar.showSnackbar("Too many test requests — wait a minute.", "warning");
        } else {
            snackbar.showSnackbar("Test event failed — check the URL.", "error");
        }
    } finally {
        testing.value = false;
    }
}
</script>
